import styled from "@emotion/styled";
import { ArrowForwardRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Theme,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import ReactTypingEffect from "react-typing-effect";
import { scrollToAnchor } from "../../Utils/scroll";
import HeroTorus from "../Scenes/HeroTorus";

const Hero = () => {
  // const navigate = useNavigate();
  const theme = useTheme();

  return (
    <StyledContainer id="hero-section">
      <HeroTorus />

      <ContentContainer
        maxWidth="xl"
        sx={{
          alignSelf: {
            xs: "start",
            sm: "start",
            md: "flex-end",
          },
          pt: { xs: "20vh", sm: "20vh", md: 0 },
        }}
      >
        <h1 className="visually-hidden">
          Enterprise expertise for your small business
        </h1>

        <StyledStack
          spacing={0}
          direction="column"
          justifyContent={"start"}
          gap={{
            xs: 1,
            sm: 1,
            md: 2,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Chip
              variant="outlined"
              sx={{
                backgroundColor: `${theme.palette.yellow.main}10`,
                borderColor: theme.palette.yellow.main,
                color: theme.palette.yellow.main,
                transformOrigin: "left center",
                textTransform: "uppercase",
                pointerEvents: "none",
                borderRadius: "4px",
                scale: {
                  xs: 0.75,
                  sm: 0.85,
                  md: 1,
                },
              }}
              label={
                <HeaderStack spacing={1.5} direction="row" alignItems="center">
                  <Typography variant="body2" color="#EEFF41" fontWeight="600">
                    DESIGN
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#EEFF41"
                    fontWeight="900"
                    fontSize="0.7rem"
                  >
                    x
                  </Typography>
                  <Typography variant="body2" color="#EEFF41" fontWeight="600">
                    DEVELOPMENT
                  </Typography>
                  <Typography
                    variant="body2"
                    color="#EEFF41"
                    fontWeight="900"
                    fontSize="0.7rem"
                  >
                    x
                  </Typography>
                  <Typography variant="body2" color="#EEFF41" fontWeight="600">
                    INNOVATION
                  </Typography>
                </HeaderStack>
              }
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <MainHeading
              variant="xl3"
              component="h2"
              sx={{
                width: {
                  xs: "88%",
                  sm: "92%",
                  md: "100%",
                },
                fontSize: {
                  xs: "1.875rem",
                  sm: "2.875rem",
                  md: "3.125rem",
                },
              }}
            >
              Enterprise Expertise for Your
            </MainHeading>
          </motion.div>

          <RotatingTextWrapper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Typography
              variant="xl2"
              component="h2"
              lineHeight={1}
              color="#EEFF41"
              sx={{
                fontSize: {
                  xs: "2.3rem",
                  sm: "3.5rem",
                  md: "4.5rem",
                },
              }}
            >
              <ReactTypingEffect
                speed={46}
                eraseSpeed={20}
                typingDelay={500}
                eraseDelay={3500}
                className="typing-effect"
                cursorClassName="typing-effect-cursor"
                text={
                  useMediaQuery(theme.breakpoints.up("md"))
                    ? [
                        "Small Business",
                        "Enterprise Project",
                        "Mobile App",
                        "Startup",
                        "Big idea",
                        "Gen AI Project",
                      ].sort(() => Math.random() - 0.5)
                    : [
                        "Small Business",
                        // "Enterprise Project", // too long for mobile
                        "Mobile App",
                        "Startup",
                        "Big idea",
                        "Gen AI Project",
                      ].sort(() => Math.random() - 0.5)
                }
                displayTextRenderer={(text: string, index: number) => {
                  const doc = new DOMParser().parseFromString(
                    text,
                    "text/html"
                  );
                  const arr = Array.from(doc.body.childNodes).map(
                    (child: any) =>
                      (child.outerHTML || child.textContent)
                        .replace("</span>", "")
                        .replace("</span", "")
                        .replace("</spa", "")
                        .replace("</sp", "")
                        .replace("</s", "")
                        .replace("</", "")
                        .replace("/", "")
                        .replace("&lt;", "")
                  );
                  return (
                    <>
                      {arr.map((item, i) => {
                        return item.indexOf("<span") > -1 ? (
                          <span
                            className={`${
                              item.toLowerCase().indexOf('cap=""') > -1
                                ? "capitalize"
                                : ""
                            } gradient`}
                            key={i}
                          >
                            {item
                              .trim()
                              .replace(' cap=""', "")
                              .replace("<span>", "")
                              .replace("<span", "")
                              .replace("<spa", "")
                              .replace("<sp", "")
                              .replace("<s", "")
                              .replace("<", "")
                              .replace("/", "")
                              .replace("&lt;", "")
                              .replace("&gt;", "")}
                          </span>
                        ) : (
                          <span key={i}>
                            {item
                              .replace("<", "")
                              .replace("</", "")
                              .replace("&lt;", "")}
                          </span>
                        );
                      })}
                    </>
                  );
                }}
              />
            </Typography>
          </RotatingTextWrapper>

          <ContentSection
            justifyContent={"space-between"}
            sx={{
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              mt: { xs: "1rem", sm: "1rem", md: "1.2rem" },
              gap: { xs: 2, md: 8 },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <DescriptionText
                variant="textXLLight"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem", md: "1.3rem" },
                }}
              >
                We design and ship exceptional software that brings
                enterprise-grade AI and development expertise to growing
                businesses of any size.
              </DescriptionText>
            </motion.div>

            <ButtonWrapper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <ActionButton
                variant="contained"
                title="Build Something Amazing"
                endIcon={<ArrowForwardRounded style={{ fontSize: 36 }} />}
                sx={{
                  fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.4rem" },
                  background: theme.palette.pink.main,
                  color: theme.palette.common.white,
                  py: { xs: 0.9, sm: 1, md: 1.5 },
                  textTransform: "none",
                  width: "fit-content",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  mt: 1,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToAnchor({ title: "Contact", link: "/#/contact" });
                }}
                disableElevation
              >
                Build Something Amazing
              </ActionButton>
            </ButtonWrapper>
          </ContentSection>

          <Stack
            sx={{
              padding: { xs: "1rem 0 2.5rem", md: "1rem 0 4rem" },
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 1.2, sm: 2, md: 7 },
              "& img": {
                height: { xs: 20, sm: 24, md: 28 },
              },
            }}
            justifyContent={{ xs: "start", sm: "start", md: "center" }}
            alignItems={{ xs: "start", sm: "start", md: "center" }}
            width={"100%"}
          >
            <Typography
              variant="body2"
              color="#EEFF41"
              sx={{
                fontSize: { xs: "0.9rem", sm: "1rem" },
                whiteSpace: "nowrap",
              }}
            >
              Trusted by
            </Typography>
            <Stack
              sx={{
                gap: { xs: 3, md: 7 },
                flexDirection: "row",
                "& img": {
                  height: { xs: 20, sm: 24, md: 28 },
                },
              }}
              alignItems={{ xs: "start", sm: "start", md: "center" }}
              width={{ xs: "100%", sm: "100%", md: "auto" }}
              justifyContent={"start"}
            >
              <img
                alt="Amazon"
                src={"/assets/brands/amazon.png"}
                loading="lazy"
                decoding="async"
                style={{
                  width: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? "auto"
                    : undefined,
                  height: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? 28
                    : "5vw",
                  objectFit: "contain",
                }}
              />
              <img
                alt="AWS"
                src={"/assets/brands/aws.png"}
                loading="lazy"
                decoding="async"
                style={{
                  width: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? "auto"
                    : undefined,
                  height: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? 28
                    : "5vw",
                  objectFit: "contain",
                }}
              />
              <img
                alt="Deloitte"
                src={"/assets/brands/deloitte.png"}
                loading="lazy"
                decoding="async"
                style={{
                  width: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? "auto"
                    : undefined,
                  height: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? 28
                    : "4vw",
                  objectFit: "contain",
                }}
              />
              <img
                alt="Microsoft"
                src={"/assets/brands/microsoft.png"}
                loading="lazy"
                decoding="async"
                style={{
                  width: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? "auto"
                    : undefined,
                  height: useMediaQuery((theme: Theme) =>
                    theme.breakpoints.up("md")
                  )
                    ? 28
                    : "5vw",
                  objectFit: "contain",
                }}
              />
            </Stack>
          </Stack>
        </StyledStack>
      </ContentContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled(Box)`
  min-height: 100vh;
  height: auto;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: space-between;

  @media (max-width: 600px) {
    min-height: auto;
`;

const ContentContainer = styled(Container)`
  align-self: flex-end;
  z-index: 200;
  padding-bottom: 0;

  .visually-hidden {
    height: 1px;
    overflow: hidden;
    width: 1px;
  }
`;

const ContentSection = styled(Stack)`
  width: 100%;
  margin-bottom: 3.5rem;

  @media (max-width: 600px) {
    margin-bottom: 0.35rem;
  }
`;

const RotatingTextWrapper = styled(motion.div)`
  background-color: #ffcc0010;
  border: 1px solid #eeff41;
  display: inline-block;
  border-radius: 4px;
  width: fit-content;
  padding: 8px 24px;

  .typing-effect-cursor {
    margin-left: 0.1rem;
    transform: scale(0.45, 0.9);
  }

  @media (max-width: 600px) {
    padding-left: 0.55rem;
    padding-right: 0.55rem;
  }
`;

const StyledStack = styled(Stack)`
  text-align: start;
  margin-top: auto;
  width: 100%;
`;

const HeaderStack = styled(Stack)`
  flex-direction: row;
  opacity: 0.8;
  margin-bottom: 0;
`;

const MainHeading = styled(Typography)<TypographyProps>`
  line-height: 1.2;
  color: #eeff41;
`;

const DescriptionText = styled(Typography)<TypographyProps>`
  color: #fff;
`;

const ButtonWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ActionButton = styled(Button)`
  color: #fff;
  &:hover {
    background-color: #ff0048;
  }
`;

export default Hero;
